import { httpClient } from './http-client';

/** Rates are fractions ("0.05" = 5%), matching the backend's storage. */
export interface RetirementPlan {
  id: string;
  household_id: string;
  name: string;
  annual_expenses: string;
  expected_return: string;
  return_stddev: string;
  inflation: string;
  retirement_date: string;
  withdrawal_rate: string;
  monthly_contribution: string;
  currency: string;
  created_at: string;
  updated_at: string;
}

export interface RetirementPlanRequest {
  name: string;
  annual_expenses: string;
  expected_return: string;
  return_stddev: string;
  inflation: string;
  retirement_date: string;
  withdrawal_rate: string;
  monthly_contribution: string;
  currency?: string;
}

/** Lever overrides re-simulate without saving the plan. */
export interface SimulateOverrides {
  monthly_contribution?: string;
  expected_return?: string;
  retirement_date?: string;
}

export interface FireBand {
  month: number;
  p10: string;
  p25: string;
  p50: string;
  p75: string;
  p90: string;
}

export interface FireSimulation {
  start_value: string;
  /** annual expenses ÷ withdrawal rate — the "25x" headline at 4%. */
  fire_number: string;
  months: number;
  retirement_month: number;
  /** Fraction of simulated paths that never depleted. */
  success_rate: string;
  nominal: FireBand[];
  real: FireBand[];
  /** One concrete unlucky trajectory (10th-percentile ending), nominal. */
  worst_decile_path: string[];
  analysis_type: string;
}

export class FireService {
  private static readonly BASE_URL = '/retirement-plans';

  static async listPlans(): Promise<RetirementPlan[]> {
    const raw = await httpClient.get<any>(FireService.BASE_URL);
    return (raw as any).data || raw;
  }

  static async createPlan(data: RetirementPlanRequest): Promise<RetirementPlan> {
    const raw = await httpClient.post<any>(FireService.BASE_URL, data);
    return (raw as any).data || raw;
  }

  static async updatePlan(id: string, data: RetirementPlanRequest): Promise<RetirementPlan> {
    const raw = await httpClient.put<any>(`${FireService.BASE_URL}/${id}`, data);
    return (raw as any).data || raw;
  }

  static async deletePlan(id: string): Promise<void> {
    await httpClient.delete(`${FireService.BASE_URL}/${id}`);
  }

  static async simulate(id: string, overrides: SimulateOverrides = {}): Promise<FireSimulation> {
    const raw = await httpClient.post<any>(`${FireService.BASE_URL}/${id}/simulate`, overrides);
    return (raw as any).data || raw;
  }
}
